import React from 'react';
import { useNavigate } from 'react-router';
import Icon from '@/components/common/atoms/Icon';
import { NotificationDto } from '@/services/notificationService';
import { formatRelativeTime, formatLocalDateTime } from '@/utils/dateTime';

/**
 * Visual treatment per backend template code. Unknown/new codes fall through to
 * the neutral default, so adding a template server-side never breaks this list.
 */
const TYPE_STYLES: Record<string, { icon: string; tone: string }> = {
  ACCOUNT_CREATED: { icon: 'UserPlus', tone: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400' },
  LEAVE_SUBMITTED: { icon: 'CalendarClock', tone: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
  LEAVE_APPROVED: { icon: 'CalendarCheck', tone: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
  LEAVE_REJECTED: { icon: 'CalendarX', tone: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400' },
  CHECKIN_REMINDER: { icon: 'Clock', tone: 'bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400' },
  PROBATION_ENDING_REMINDER: { icon: 'BadgeCheck', tone: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' },
  OFFBOARDING_INITIATED: { icon: 'DoorOpen', tone: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
  OFFBOARDING_APPROVED: { icon: 'ShieldCheck', tone: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
  OFFBOARDING_CLOSED: { icon: 'Ban', tone: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400' },
  OFFBOARDING_COMPLETED: { icon: 'LogOut', tone: 'bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300' },
  OFFBOARDING_AWAITING_HR: { icon: 'ShieldQuestion', tone: 'bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400' },
  OFFBOARDING_IT_CLEARANCE_DUE: { icon: 'Laptop', tone: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400' },
  OFFBOARDING_LWD_REVISED: { icon: 'CalendarClock', tone: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
  OFFBOARDING_INTERVIEW_SCHEDULED: { icon: 'MessageSquare', tone: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' },
  OFFBOARDING_INTERVIEW_ASSIGNED: { icon: 'UserRoundCheck', tone: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' },
  OFFBOARDING_READY_TO_COMPLETE: { icon: 'ClipboardCheck', tone: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
};

const DEFAULT_STYLE = {
  icon: 'Bell',
  tone: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300',
};

/**
 * Where a notification takes you. Matched by template-code prefix so a new server-side
 * code in a known family routes without touching this list; unknown families get no link.
 * `referenceId` is the record the notification is about (offboarding id, leave request id…).
 */
export const routeForNotification = (n: Pick<NotificationDto, 'notificationType' | 'referenceId'>): string | null => {
  const t = n.notificationType;
  if (t.startsWith('OFFBOARDING_')) return n.referenceId ? `/offboarding/${n.referenceId}` : '/offboarding';
  if (t.startsWith('LEAVE_')) return '/leave';
  if (t.startsWith('TICKET_')) return '/ticket-management';
  if (t === 'CHECKIN_REMINDER') return '/attendance';
  if (t === 'PROBATION_ENDING_REMINDER') return '/settings/users';
  if (t === 'ACCOUNT_CREATED') return '/profile';
  return null;
};

interface NotificationItemProps {
  notification: NotificationDto;
  /** Whether the full message body is shown. */
  expanded: boolean;
  onToggle: (id: string) => void;
  /** Called after navigating to the linked record — the dropdown uses it to close itself. */
  onOpen?: (id: string) => void;
}

/**
 * One notification row, shared by the navbar dropdown and the notifications page.
 *
 * Message bodies are stripped-HTML email copy — often several paragraphs — so the
 * row collapses to two lines and expands in place rather than truncating for good.
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ notification: n, expanded, onToggle, onOpen }) => {
  const navigate = useNavigate();
  const style = TYPE_STYLES[n.notificationType] ?? DEFAULT_STYLE;
  const route = routeForNotification(n);

  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!route) return;
    if (!n.isRead) onToggle(n.id); // parent marks it read
    onOpen?.(n.id);
    navigate(route);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onToggle(n.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(n.id); } }}
      aria-expanded={expanded}
      className={`w-full px-4 py-3 text-left cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/60 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
        !n.isRead ? 'bg-indigo-50/60 dark:bg-indigo-950/20' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${style.tone}`}>
          <Icon name={style.icon} size={16} />
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <p
              className={`flex-1 text-sm text-gray-900 dark:text-white ${expanded ? '' : 'truncate'} ${
                n.isRead ? 'font-medium' : 'font-semibold'
              }`}
            >
              {n.title}
            </p>
            {!n.isRead && (
              <span
                className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"
                aria-label="Unread"
              />
            )}
          </div>

          <p
            className={`text-xs text-gray-600 dark:text-gray-300 mt-1 ${
              expanded ? 'whitespace-pre-wrap break-words' : 'line-clamp-2'
            }`}
          >
            {n.message}
          </p>

          <div className="flex items-center justify-between gap-2 mt-1.5">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {expanded ? formatLocalDateTime(n.createdAt) : formatRelativeTime(n.createdAt)}
            </p>
            {route && (
              <button
                type="button"
                onClick={open}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Open <Icon name="ArrowRight" size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
